// src/utils/scroll.ts

/** Cuánto tarda el viaje. Más largo que la animación de despliegue (300ms). */
const DURACION = 420;

/** easeOutCubic: arranca rápido y frena al llegar. */
const suavizar = (t: number) => 1 - Math.pow(1 - t, 3);

const prefiereMenosMovimiento = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Dónde tendría que quedar el scroll para que `el` toque el borde superior.
 *
 * El margen sale de `scroll-margin-top`, o sea de la clase `scroll-mt-*` que
 * lleva el elemento: la barra superior es sticky y taparía lo de arriba. Se lee
 * del CSS en vez de repetir el número acá.
 */
const destinoDe = (el: HTMLElement) => {
  const margen = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  return el.getBoundingClientRect().top + window.scrollY - margen;
};

/** Sólo puede haber un viaje en curso; el nuevo pisa al anterior. */
let cancelarEnCurso: (() => void) | null = null;

/**
 * Lleva un elemento hasta el borde superior de la ventana.
 *
 * Reemplaza a `scrollIntoView({ behavior: "smooth" })`, que acá fallaba por dos
 * cosas que pasan al mismo tiempo: la fila se despliega con una animación de
 * 300ms mientras el scroll ya está viajando, y el nativo calcula el destino una
 * sola vez, al arrancar. Encima, cuando el alto del documento cambia en el
 * medio —que es exactamente lo que hace la fila al abrirse— el navegador puede
 * dar por cancelado el scroll suave y dejarlo donde esté.
 *
 * Eso explica el "hay que tocar dos veces": el segundo toque encontraba la fila
 * ya abierta, así que no había animación ni cambio de alto que lo interrumpiera.
 * En escritorio no se notaba porque las dos tarjetas van lado a lado y nada de
 * lo de arriba se corre; apiladas en mobile, sí.
 *
 * Esta versión recalcula el destino en cada frame, así que le da igual que la
 * página se mueva debajo, y no hay animación nativa que el navegador pueda
 * cortar por las suyas: la corta el usuario y nadie más.
 *
 * @returns una función para cancelar el viaje (al desmontar, por ejemplo).
 */
export const irAlElemento = (el: HTMLElement): (() => void) => {
  cancelarEnCurso?.();

  let frame = 0;
  let vivo = true;

  const terminar = () => {
    if (!vivo) return;
    vivo = false;
    cancelAnimationFrame(frame);
    window.removeEventListener("wheel", terminar);
    window.removeEventListener("touchstart", terminar);
    window.removeEventListener("keydown", terminar);
    if (cancelarEnCurso === terminar) cancelarEnCurso = null;
  };

  if (prefiereMenosMovimiento()) {
    window.scrollTo({ top: destinoDe(el), behavior: "auto" });
    return terminar;
  }

  const inicio = window.scrollY;
  const arranque = performance.now();

  const paso = (ahora: number) => {
    // Si la fila se fue del DOM (cambio de página, desmontaje) no hay adónde ir
    if (!el.isConnected) {
      terminar();
      return;
    }

    const avance = Math.min(1, (ahora - arranque) / DURACION);
    const destino = destinoDe(el);
    // "auto" explícito: si algún día el CSS pone scroll-behavior: smooth, cada
    // frame arrancaría su propia animación y esto se volvería un temblor.
    window.scrollTo({
      top: inicio + (destino - inicio) * suavizar(avance),
      behavior: "auto",
    });

    if (avance < 1) frame = requestAnimationFrame(paso);
    else terminar();
  };

  // El usuario manda: si toca o rueda, lo soltamos donde esté.
  window.addEventListener("wheel", terminar, { passive: true });
  window.addEventListener("touchstart", terminar, { passive: true });
  window.addEventListener("keydown", terminar);

  cancelarEnCurso = terminar;
  frame = requestAnimationFrame(paso);

  return terminar;
};
